/**
 * A base tooltip component that handles positioning
 */
import React, { useState, useLayoutEffect, useRef } from 'react';

function BaseToolTip({ children, preferredPlacement = 'bottom', width, yOffset, xOffset }) {
    const [isMounted, setIsMounted] = useState(false);
    const tooltipRef = useRef(null);
    const containerRef = useRef(null);

    // Set mounted state
    useLayoutEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false);
    }, []);

    const positionClasses = {
        bottom: 'bottom-0 translate-y-full left-1/2 -translate-x-1/2',
        top: 'top-0 -translate-y-full left-1/2 -translate-x-1/2',
        left: 'left-0 -translate-x-full top-1/2 -translate-y-1/2',
        right: 'right-0 translate-x-full top-1/2 -translate-y-1/2'
    };

    const dynamicStyles = {
        pointerEvents: 'none',
        whiteSpace: 'normal',
        maxWidth: width ? `${width}px` : '300px',
        width: width ? `${width}px` : 'max-content',
    };

    const defaultOffset = '8px';

    switch (preferredPlacement) {
        case 'bottom':
            dynamicStyles.marginTop = yOffset ? `${yOffset}px` : defaultOffset;
            break;
        case 'top':
            dynamicStyles.marginTop = yOffset ? `-${yOffset}px` : `-${defaultOffset}`;
            break;
        case 'left':
            dynamicStyles.marginLeft = xOffset ? `-${xOffset}px` : `-${defaultOffset}`;
            break;
        case 'right':
            dynamicStyles.marginLeft = xOffset ? `${xOffset}px` : defaultOffset;
            break;
        default:
            break;
    }

    return (
        <div ref={containerRef} className="relative group" style={{ overflow: 'visible' }}>
            {isMounted && (
                <div
                    ref={tooltipRef}
                    className={`
                        absolute 
                        ${positionClasses[preferredPlacement]}
                        hidden 
                        group-hover:block 
                        z-[9999]
                        bg-white 
                        rounded-lg 
                        border
                        border-gray-200
                        shadow-lg
                        transition-opacity
                        duration-150
                        ease-in-out
                    `}
                    style={dynamicStyles}
                >
                    <div className="p-3">
                        <p className="text-sm text-gray-600">
                            {children}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Tooltip components using the base component with different preferred placements
 */
export function ToolTipBottom({ children, width, yOffset }) {
    if (!children) return null;
    return <BaseToolTip preferredPlacement="bottom" width={width} yOffset={yOffset}>{children}</BaseToolTip>;
}

export function ToolTipTop({ children, width, yOffset }) {
    if (!children) return null;
    return <BaseToolTip preferredPlacement="top" width={width} yOffset={yOffset}>{children}</BaseToolTip>;
}

export function ToolTipLeft({ children, width, xOffset }) {
    if (!children) return null;
    return <BaseToolTip preferredPlacement="left" width={width} xOffset={xOffset}>{children}</BaseToolTip>;
}

export function ToolTipRight({ children, width, xOffset }) {
    if (!children) return null;
    return <BaseToolTip preferredPlacement="right" width={width} xOffset={xOffset}>{children}</BaseToolTip>;
}

/**
 * Container component
 */
export function ToolTipHoverContainer({ children }) {
    return (
        <div className="relative group" style={{ overflow: 'visible' }}>
            {children}
        </div>
    );
}